import React, { useState, useRef, useEffect } from "react";
import {
    Button, Card, DatePicker, Space, Table, Tag,
    Modal, Select, Input, Divider, Spin, Row, Col, Dropdown
} from "antd";
import {
    CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
    SearchOutlined, PlusOutlined, EllipsisOutlined,
    FileProtectOutlined, DownOutlined
} from "@ant-design/icons";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import { getParametre, rejectDecaissement, validateDecaissement } from "../../hooks/useHooks";
import FixEnginCategorieModal from "./components/FixEnginCategorieModal";
import AntidatageDateReglement, { messageRefusAntidatage } from "./components/AntidatageDateReglement";
import { PARAMETERS } from "../../variables/constants";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { frenchDate } from "../../variables/functions";
import { highlightMatch } from "../../variables/stringFunctions";
import { searchSinistre } from "../../hooks/useSearch";

const { RangePicker } = DatePicker;
const { confirm } = Modal;

// ✅ Déplacé hors du composant
const Text = ({ children, ...props }) => <span {...props}>{children}</span>;

const displayNumbers = (num) =>
    new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num || 0);

const ValidationDecaissements = () => {
    const [decaissements, setDecaissements] = useState([]);
    const [decaissementsLoading, setDecaissementsLoading] = useState(false);
    const [motifsLoading, setMotifsLoading] = useState(false); // ✅ loading séparé pour les motifs
    const [modalLoading, setModalLoading] = useState(false);   // ✅ loading séparé pour le modal
    const [selectedDecaissement, setSelectedDecaissement] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [filteredInfo, setFilteredInfo] = useState({});
    const [total, setTotal] = useState(0);
    const [motifs, setMotifs] = useState([]);
    const [options, setOptions] = useState([]);               // ✅ initialisé vide, sync via useEffect
    const [modalAnnulation, setModalAnnulation] = useState(false);
    const [motifAnnulation, setMotifAnnulation] = useState("");
    const [categorie, setCategorie] = useState(null);
    const [montantMinimum, setMontantMinimum] = useState(null);
    const [montantMaximum, setMontantMaximum] = useState(null);
    const [numeroCheque, setNumeroCheque] = useState(null);
    const [compagnie, setCompagnie] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [critere, setCritere] = useState('');
    const [fixCategorieData, setFixCategorieData] = useState(null); // {enginCodeId, immatriculation, decaissementCodeId}
    const [search, setSearch] = useState('');
    const [membres, setMembres] = useState([]);
    const [name, setName] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searchValue, setSearchValue] = useState("");

    const inputRef = useRef(null);
    const navigate = useNavigate();

    // ✅ Sync options quand motifs est chargé
    useEffect(() => { setOptions(motifs); }, [motifs]);

    useEffect(() => {
        const fetchMotifs = async () => {
            setMotifsLoading(true);
            const response = await getParametre(PARAMETERS.motifAnnulationCheque);
            setMotifs(response.data);
            setMotifsLoading(false);
        };
        fetchMotifs();

        axiosInstance.get(`/api/organisme/assurance`).then((res) => {
            const membs = res.data.map((memb, index) => ({
                key: index,
                text: memb.raisonSociale,
                label: memb.raisonSociale,
                value: memb.code,
                data: memb,
            }));
            membs.push({ key: "POOL", text: "POOL", label: "POOL", value: "00" });
            setMembres(membs);
        }).catch(console.error);
    }, []);

    const fetchAllDecaissements = async (params = {}) => {
        setDecaissementsLoading(true);
        try {
            const response = await axiosInstance.get(`/api/decaissement/sinistre`, {
                params: {
                    page: params.pagination.current - 1,
                    size: params.pagination.pageSize,
                    ...params.filters,
                    categorie,
                    montantMin: montantMinimum,
                    montantMax: montantMaximum,
                    numeroCheque,
                    startDate,
                    endDate,
                    compagnie,
                },
            });
            const { content, totalElements } = response.data;
            setDecaissements(content);
            setPagination({ ...params.pagination, total: totalElements });
            setTotal(totalElements);
        } catch (err) {
            console.error(err);
        } finally {
            setDecaissementsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllDecaissements({ pagination, filteredInfo });
    }, [
        pagination.current, pagination.pageSize,
        numeroCheque, categorie, montantMinimum,
        montantMaximum, startDate, endDate, compagnie
    ]);

    const handleChange = (pag, filters) => {
        setPagination(pag);
        setFilteredInfo(filters);
    };

    const handleValider = (record) => {
        const dateAntidatage = { current: null };
        confirm({
            title: "Validation du décaissement",
            icon: <CheckCircleOutlined style={{ color: '#2ECC71', fontSize: '24px' }} />,
            content: (
                <div style={{ padding: '16px 0' }}>
                    <p style={{ marginBottom: '12px', color: '#333333' }}>
                        Voulez-vous vraiment valider ce décaissement ?
                    </p>
                    <Card size="small" style={{ backgroundColor: '#F9FBF9' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div><strong>Sinistre:</strong> {record.numeroSinistreM || record.numeroSinistreP}</div>
                            <div><strong>Bénéficiaire:</strong> {record.beneficiaire}</div>
                            <div><strong>Montant:</strong> {displayNumbers(record.montantPaye)} F CFA</div>
                        </Space>
                    </Card>
                    <AntidatageDateReglement dateRef={dateAntidatage} />
                </div>
            ),
            okText: "Valider",
            cancelText: "Annuler",
            okButtonProps: { style: { backgroundColor: '#2ECC71', borderColor: '#2ECC71' } },
            async onOk() {
                try {
                    await validateDecaissement(record.codeId, dateAntidatage.current);
                    toast.success('Décaissement validé avec succès');
                    fetchAllDecaissements({ pagination, filteredInfo });
                } catch (e) {
                    const data = e?.response?.data;
                    if (e?.response?.status === 422 && data?.code === "ENGIN_CATEGORIE_MANQUANTE") {
                        if (data.enginCodeId) {
                            setFixCategorieData({
                                enginCodeId: data.enginCodeId,
                                immatriculation: data.immatriculation,
                                decaissementCodeId: record.codeId,
                                dateReglement: dateAntidatage.current,
                            });
                        } else {
                            toast.warning(data.message || "Aucun véhicule n'est rattaché à ce sinistre. Rattachez le véhicule concerné avant de régler.");
                        }
                    } else if (e?.response?.status === 422 && data?.code === "SAP_DEPASSE") {
                        toast.error(data.message || "Montant supérieur au SAP disponible du sinistre.");
                    } else if (messageRefusAntidatage(e)) {
                        toast.error(messageRefusAntidatage(e));
                    } else {
                        console.error(e);
                        toast.error("Erreur lors de la validation");
                    }
                }
            }
        });
    };

    const handleRejeter = (record) => {
        confirm({
            title: "Rejet du décaissement",
            icon: <CloseCircleOutlined style={{ color: '#E74C3C', fontSize: '24px' }} />,
            content: (
                <div style={{ padding: '16px 0' }}>
                    <p style={{ marginBottom: '12px', color: '#333333' }}>
                        Voulez-vous vraiment rejeter ce décaissement ?
                    </p>
                    <Card size="small" style={{ backgroundColor: '#F9FBF9' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div><strong>Sinistre:</strong> {record.numeroSinistreM || record.numeroSinistreP}</div>
                            <div><strong>Bénéficiaire:</strong> {record.beneficiaire}</div>
                            <div><strong>Montant:</strong> {displayNumbers(record.montantPaye)} F CFA</div>
                        </Space>
                    </Card>
                </div>
            ),
            okText: "Rejeter",
            cancelText: "Annuler",
            okButtonProps: { danger: true },
            async onOk() {
                try {
                    await rejectDecaissement(record.codeId);
                    toast.success('Décaissement rejeté avec succès');
                    fetchAllDecaissements({ pagination, filteredInfo }); // ✅ pas de reload
                } catch (e) {
                    console.error(e);
                    toast.error("Erreur lors du rejet");
                }
            }
        });
    };

    const handleAnnulerCheque = async () => {
        if (!motifAnnulation) {
            toast.warning("Veuillez sélectionner un motif");
            return;
        }
        setModalLoading(true);
        try {
            // TODO: appel API annulation
            toast.success("Chèque annulé avec succès !");
            setModalAnnulation(false);
            setMotifAnnulation("");
            fetchAllDecaissements({ pagination, filteredInfo });
        } catch (e) {
            toast.error("Erreur lors de l'annulation");
        } finally {
            setModalLoading(false);
        }
    };

    const addItem = (e) => {
        e.preventDefault();
        if (!name?.trim()) return;
        setOptions((prev) => [...prev, { label: name, value: name }]);
        setName("");
        inputRef.current?.focus();
    };

    const handleSearch = () => {
        if (critere === "NUM_CHEQUE") setNumeroCheque(search || null);
        else if (critere === "MONTANT_MIN") setMontantMinimum(search ? parseInt(search) : null);
        else if (critere === "MONTANT_MAX") setMontantMaximum(search ? parseInt(search) : null);
    };

    const handleDateFilter = (_, [sDate, eDate]) => {
        setStartDate(sDate || null);
        setEndDate(eDate || null);
    };

    useEffect(() => {
        if (searchValue?.length > 2) {
            searchSinistre(searchValue)
                .then((res) => setSearchResults(res.data.content))
                .catch(console.error);
        }
    }, [searchValue]);

    const renderSearchResults = (results) => (
        <div className="px-3 py-2">
            {results.map((item, index) => (
                <div
                    key={index}
                    onClick={() => navigate("/details-sinistre/v2", { state: { codeId: item.codeId } })}
                    className="dropdown-item d-flex align-items-center gap-2 py-1"
                    style={{ cursor: "pointer" }}
                >
                    <span className="text-truncate">
                        {[
                            item?.numeroSinistrem || item?.numSinistre,
                            item?.numeroPoliceM || item?.numeroPoliceP,
                            frenchDate(item?.dateSinistre),
                            item?.immatriculation,
                            item?.nom,
                            item?.prenom || "",
                        ].map((text, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && " - "}
                                {highlightMatch(text?.toString(), searchValue)}
                            </React.Fragment>
                        ))}
                    </span>
                </div>
            ))}
        </div>
    );

    // ✅ Menu du bouton "Règlement multiple"
    const reglementMenuItems = [
        {
            key: 'experts',
            label: 'Les experts',
            onClick: () => navigate('/paiements-sinistre-expert'),
        },
        {
            key: 'entites',
            label: 'Les entités de constat',
            onClick: () => navigate('/paiements-sinistre-entites'),
        },
        {
            key: 'conseils',
            label: 'Les conseils juridiques',
            onClick: () => navigate('/paiements-sinistre-conseil-juridique/v2'),
        },
        {
            key: 'etablissements',
            label: 'Les établissements (pharmacies, cliniques…)',
            onClick: () => navigate('/paiements-sinistre-etablissement/v2'),
        },
    ];

    const columns = [
        {
            title: "N° Sinistre",
            key: "numeroSinistre",
            width: 140,
            render: (_, record) => (
                <Text strong style={{ color: '#333333', fontFamily: 'monospace' }}>
                    {record.numeroSinistreM || record.numeroSinistreP}
                </Text>
            ),
        },
        {
            title: "N° Police",
            key: "numeroPolice",
            width: 140,
            render: (_, record) => (
                <Text style={{ color: '#666', fontFamily: 'monospace' }}>
                    {record.numeroPoliceM || record.numeroPoliceP}
                </Text>
            ),
        },
        {
            title: "Souscripteur",
            dataIndex: "souscripteur",
            key: "souscripteur",
            render: (text) => <Text style={{ color: '#333333' }}>{text}</Text>,
        },
        {
            title: "Bénéficiaire",
            key: "beneficiaire",
            render: (_, record) => (
                <div>
                    <Text strong style={{ color: '#333333' }}>{record.beneficiaire}</Text>
                    {record.numeroCheque && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            Chèque N° {record.numeroCheque} - {record.banque}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Montant",
            dataIndex: "montantPaye",
            key: "montantPaye",
            width: 150,
            align: "right",
            render: (montant) => (
                <Text strong style={{ color: 'black', fontSize: '15px' }}>
                    {displayNumbers(montant)} F
                </Text>
            ),
        },
        {
            title: "État",
            dataIndex: "etatDecaissement",
            key: "etatDecaissement",
            width: 130,
            render: (etat) => {
                const config = {
                    VALIDE: { color: '#2ECC71', bg: '#A8E6CF', label: 'Validé' },
                    EN_ATTENTE: { color: '#F39C12', bg: '#FFF3E0', label: 'En attente' },
                    REJETE: { color: '#E74C3C', bg: '#FADBD8', label: 'Rejeté' },
                    ENCAISSE: { color: '#145A32', bg: '#D5F4E6', label: 'Encaissé' },
                };
                const style = config[etat] || config['EN_ATTENTE'];
                return (
                    <Tag style={{
                        backgroundColor: style.bg, color: style.color,
                        borderRadius: '6px', padding: '4px 12px', fontWeight: 600,
                    }}>
                        {style.label}
                    </Tag>
                );
            },
        },
        {
            title: "Date",
            dataIndex: "dateReglement",
            key: "dateReglement",
            width: 120,
            render: (date) => (
                <Text style={{ color: '#666' }}>
                    {date ? new Date(date).toLocaleDateString('fr-FR') : '-'}
                </Text>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 80,
            align: "center",
            render: (_, record) => {
                const menuItems = [];
                if (record.etatDecaissement === "EN_ATTENTE") {
                    menuItems.push(
                        { key: 'valider', icon: <CheckCircleOutlined style={{ color: '#2ECC71' }} />, label: 'Valider', onClick: () => handleValider(record) },
                        { key: 'rejeter', icon: <CloseCircleOutlined style={{ color: '#E74C3C' }} />, label: 'Rejeter', onClick: () => handleRejeter(record) }
                    );
                }
                menuItems.push({
                    key: 'details', icon: <EyeOutlined style={{ color: '#2ECC71' }} />,
                    label: 'Voir détails', onClick: () => alert('Détails du décaissement')
                });
                if (record.etatDecaissement === "ENCAISSE") {
                    menuItems.push({
                        key: 'annuler', icon: <CloseCircleOutlined style={{ color: '#E74C3C' }} />,
                        label: 'Annuler chèque',
                        onClick: () => { setSelectedDecaissement(record); setModalAnnulation(true); }
                    });
                }
                return (
                    <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                        <Button type="text" icon={<EllipsisOutlined />} style={{ color: '#2ECC71' }} />
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar
                        filteredResults={searchResults}
                        setFilteredResults={setSearchResults}
                        renderSearchResults={renderSearchResults}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                    />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ minHeight: '100vh', backgroundColor: '#F9FBF9', padding: '24px' }}>
                                <div style={{ maxWidth: '1600px', margin: '0 auto' }}>

                                    {/* En-tête */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                            <div style={{
                                                width: '56px', height: '56px', borderRadius: '14px',
                                                background: 'linear-gradient(135deg, #2ECC71 0%, #145A32 100%)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                boxShadow: '0 6px 20px rgba(46, 204, 113, 0.4)',
                                            }}>
                                                <FileProtectOutlined style={{ fontSize: '28px', color: 'white' }} />
                                            </div>
                                            <div>
                                                <h1 style={{ margin: 0, fontSize: '32px', fontWeight: 700, color: '#333333' }}>
                                                    Validation des Décaissements
                                                </h1>
                                                <p style={{ margin: 0, color: '#666', fontSize: '15px' }}>
                                                    Gestion et validation des paiements sinistres
                                                </p>
                                            </div>
                                        </div>

                                        {/* ✅ Bouton "Règlement multiple" avec dropdown */}
                                        <Dropdown menu={{ items: reglementMenuItems }} trigger={['click']}>
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                style={{ background: '#2ECC71', border: 'none' }}
                                            >
                                                Règlement multiple <DownOutlined />
                                            </Button>
                                        </Dropdown>
                                    </div>

                                    {/* Filtres */}
                                    <Card bordered={false} style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                        <Row gutter={[16, 16]} align="middle">
                                            <Col xs={24} md={8}>
                                                <RangePicker
                                                    onChange={handleDateFilter}
                                                    style={{ width: '100%', borderRadius: '8px' }}
                                                    size="large"
                                                    placeholder={["Date début", "Date fin"]}
                                                />
                                            </Col>
                                            <Col xs={24} md={8}>
                                                {critere === "COMPAGNIE" ? (
                                                    <Select
                                                        placeholder="Sélectionner une compagnie"
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                        options={membres}
                                                        onChange={(value) => setCompagnie(value)} // ✅ direct
                                                    />
                                                ) : (
                                                    <Input
                                                        placeholder="Rechercher..."
                                                        size="large"
                                                        style={{ borderRadius: '8px' }}
                                                        value={search}
                                                        onChange={(e) => setSearch(e.target.value)}
                                                        prefix={<SearchOutlined style={{ color: '#2ECC71' }} />}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                                    />
                                                )}
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Select
                                                    placeholder="Critère de recherche"
                                                    style={{ width: '100%' }}
                                                    size="large"
                                                    value={critere}
                                                    onChange={(value) => {
                                                        setNumeroCheque(null);
                                                        setMontantMaximum(null);
                                                        setMontantMinimum(null);
                                                        setCompagnie(null);
                                                        setSearch('');
                                                        setCritere(value);
                                                    }}
                                                    options={[
                                                        { label: "Tout", value: "" },
                                                        { label: "Numéro de chèque", value: "NUM_CHEQUE" },
                                                        { label: "Montant minimum", value: "MONTANT_MIN" },
                                                        { label: "Montant maximum", value: "MONTANT_MAX" },
                                                        { label: "Compagnie", value: "COMPAGNIE" },
                                                    ]}
                                                />
                                            </Col>
                                        </Row>
                                    </Card>

                                    {/* Tableau */}
                                    <Card bordered={false} style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                        <Table
                                            columns={columns}
                                            dataSource={decaissements}
                                            rowKey="codeId"
                                            loading={decaissementsLoading}
                                            onChange={handleChange}
                                            pagination={{
                                                current: pagination.current,
                                                pageSize: pagination.pageSize,
                                                showSizeChanger: true,
                                                total,
                                                showTotal: (t) => `Total : ${t} décaissements`,
                                            }}
                                        />
                                    </Card>

                                    {/* Modal Annulation */}
                                    <Modal
                                        title={
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#333333' }}>
                                                <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#FADBD8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <CloseCircleOutlined style={{ color: '#E74C3C', fontSize: '18px' }} />
                                                </div>
                                                Annulation de chèque
                                            </div>
                                        }
                                        open={modalAnnulation}
                                        onCancel={() => { setModalAnnulation(false); setMotifAnnulation(""); }}
                                        onOk={handleAnnulerCheque}
                                        okText="Confirmer l'annulation"
                                        cancelText="Annuler"
                                        okButtonProps={{ danger: true, style: { borderRadius: '8px', fontWeight: 600 } }}
                                        cancelButtonProps={{ style: { borderRadius: '8px' } }}
                                        width={600}
                                        confirmLoading={modalLoading}
                                    >
                                        <div style={{ padding: '24px 0' }}>
                                            {/* ✅ Afficher les infos du décaissement sélectionné */}
                                            {selectedDecaissement && (
                                                <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#F9FBF9' }}>
                                                    <Space direction="vertical" style={{ width: '100%' }}>
                                                        <div><strong>Sinistre:</strong> {selectedDecaissement.numeroSinistreM || selectedDecaissement.numeroSinistreP}</div>
                                                        <div><strong>Bénéficiaire:</strong> {selectedDecaissement.beneficiaire}</div>
                                                        <div><strong>Montant:</strong> {displayNumbers(selectedDecaissement.montantPaye)} F CFA</div>
                                                    </Space>
                                                </Card>
                                            )}
                                            <p style={{ marginBottom: '16px', color: '#666' }}>
                                                Veuillez préciser le motif de l'annulation du chèque
                                            </p>
                                            <Select
                                                placeholder="Sélectionner un motif"
                                                style={{ width: '100%' }}
                                                size="large"
                                                value={motifAnnulation || undefined}
                                                onChange={(value) => setMotifAnnulation(value)}
                                                loading={motifsLoading}
                                                dropdownRender={(menu) => (
                                                    <>
                                                        {menu}
                                                        <Divider style={{ margin: '8px 0' }} />
                                                        <Space style={{ padding: '0 8px 4px', width: '100%' }}>
                                                            <Input
                                                                placeholder="Nouveau motif"
                                                                ref={inputRef}
                                                                value={name}
                                                                onChange={(e) => setName(e.target.value)}
                                                                style={{ flex: 1 }}
                                                            />
                                                            <Button type="primary" icon={<PlusOutlined />} onClick={addItem}
                                                                style={{ backgroundColor: '#2ECC71', borderColor: '#2ECC71' }}>
                                                                Ajouter
                                                            </Button>
                                                        </Space>
                                                    </>
                                                )}
                                                options={options}
                                            />
                                        </div>
                                    </Modal>
                                </div>
                            </div>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
            {fixCategorieData && (
                <FixEnginCategorieModal
                    open={!!fixCategorieData}
                    enginCodeId={fixCategorieData.enginCodeId}
                    immatriculation={fixCategorieData.immatriculation}
                    onFixed={async () => {
                        const { decaissementCodeId, dateReglement } = fixCategorieData;
                        setFixCategorieData(null);
                        try {
                            await validateDecaissement(decaissementCodeId, dateReglement);
                            toast.success('Décaissement validé avec succès');
                            fetchAllDecaissements({ pagination, filteredInfo });
                        } catch (e) {
                            toast.error(messageRefusAntidatage(e) || "Erreur lors de la validation après correction");
                        }
                    }}
                    onClose={() => setFixCategorieData(null)}
                />
            )}
        </div>
    );
};

export default ValidationDecaissements;